import React, { useState, useEffect, useCallback } from "react";
import Button from "@mui/material/Button";
import MDBox from "shared/ui/Box/MDBox";
import MDTypography from "shared/ui/Typography/MDTypography";
import { Grid, TextField } from "@mui/material";
import usePlanner from "features/planner/state/usePlanner";
import { createInputHandler } from "features/planner/utils/inputValidation";
import EditModeBulkSelectionButtons from "features/planner/components/editmode/EditModeBulkSelectionButtons";
import EditModeClearFieldButton from "./EditModeClearFieldButton";
import EditModeClearAllButton from "./EditModeClearAllButton";
import { alpha } from "@mui/material/styles";

export default function EditModeActionsPanel() {
  const {
    selectedSlots,
    exitEditMode,
    applyBatchEdit,
    clearSelectedSlots,
    selectAllSlots,
    clearBatchField,
    clearAllBatchFields,
  } = usePlanner();

  const [form, setForm] = useState({
    sets: "",
    reps: "",
    rest: "",
    intensity: "",
    tempo: "",
    notes: "",
  });

  const handleApply = useCallback(() => {
    if (selectedSlots.length === 0) return;

    applyBatchEdit(form);

    setForm({
      sets: "",
      reps: "",
      rest: "",
      intensity: "",
      tempo: "",
      notes: "",
    });
  }, [applyBatchEdit, form, selectedSlots.length]);

  // 🔥 Enter global en modo edición
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "Enter" && selectedSlots.length > 0) {
        e.preventDefault();
        handleApply();
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [handleApply, selectedSlots.length]);

  return (
    <MDBox
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        width: "100%",
        alignItems: "center",
      }}
    >
      <MDTypography
        variant="h6"
        align="center"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          "&::before": {
            content: '""',
            width: 5,
            height: 16,
            borderRadius: "999px",
            backgroundColor: "accent.main",
          },
        }}
      >
        Editar parámetros
      </MDTypography>
      <MDTypography
        variant="caption"
        align="center"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5 }}
      >
        Los parámetros se aplicarán sobre los ejercicios seleccionados
      </MDTypography>

      <MDTypography variant="caption" align="center">
        {selectedSlots.length} ejercicios seleccionados
      </MDTypography>

      <EditModeBulkSelectionButtons onSelectAll={selectAllSlots} onClearAll={clearSelectedSlots} />

      {/* Inputs de parámetros */}
      <MDBox sx={{ width: "100%", mt: 1 }}>
        <Grid container spacing={1}>
          {/* SERIES */}
          <Grid item xs={12}>
            <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <MDBox sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Series"
                  fullWidth
                  size="small"
                  value={form.sets}
                  onChange={createInputHandler("sets", setForm)}
                  inputProps={{ maxLength: 2 }}
                  InputProps={{ sx: { height: 34, fontSize: "0.8rem" } }}
                  InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                />
              </MDBox>
              <EditModeClearFieldButton onClear={() => clearBatchField("sets")} />
            </MDBox>
          </Grid>

          {/* REPS */}
          <Grid item xs={12}>
            <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <MDBox sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Reps"
                  fullWidth
                  size="small"
                  value={form.reps}
                  onChange={createInputHandler("reps", setForm)}
                  inputProps={{ maxLength: 5 }}
                  InputProps={{ sx: { height: 34, fontSize: "0.8rem" } }}
                  InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                />
              </MDBox>
              <EditModeClearFieldButton onClear={() => clearBatchField("reps")} />
            </MDBox>
          </Grid>

          {/* REST */}
          <Grid item xs={12}>
            <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <MDBox sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Descanso"
                  fullWidth
                  size="small"
                  value={form.rest}
                  onChange={createInputHandler("rest", setForm)}
                  inputProps={{ maxLength: 3 }}
                  InputProps={{ sx: { height: 34, fontSize: "0.8rem" } }}
                  InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                />
              </MDBox>
              <EditModeClearFieldButton onClear={() => clearBatchField("rest")} />
            </MDBox>
          </Grid>

          {/* INTENSITY */}
          <Grid item xs={12}>
            <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <MDBox sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Intensidad"
                  fullWidth
                  size="small"
                  value={form.intensity}
                  onChange={createInputHandler("intensity", setForm)}
                  inputProps={{ maxLength: 8 }}
                  InputProps={{ sx: { height: 34, fontSize: "0.8rem" } }}
                  InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                />
              </MDBox>
              <EditModeClearFieldButton onClear={() => clearBatchField("intensity")} />
            </MDBox>
          </Grid>

          {/* TEMPO */}
          <Grid item xs={12}>
            <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <MDBox sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Tempo"
                  fullWidth
                  size="small"
                  value={form.tempo}
                  onChange={createInputHandler("tempo", setForm)}
                  inputProps={{ maxLength: 8 }}
                  InputProps={{ sx: { height: 34, fontSize: "0.8rem" } }}
                  InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                  placeholder="3-1-1-0"
                />
              </MDBox>
              <EditModeClearFieldButton onClear={() => clearBatchField("tempo")} />
            </MDBox>
          </Grid>

          {/* NOTES */}
          <Grid item xs={12}>
            <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <MDBox sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Nota"
                  fullWidth
                  size="small"
                  value={form.notes}
                  onChange={createInputHandler("notes", setForm)}
                  inputProps={{ maxLength: 15 }}
                  InputProps={{ sx: { height: 34, fontSize: "0.8rem" } }}
                  InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                  placeholder="dropset, tempo..."
                />
              </MDBox>
              <EditModeClearFieldButton onClear={() => clearBatchField("notes")} />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      <EditModeClearAllButton onClear={clearAllBatchFields} />

      <MDBox sx={{ flexGrow: 1 }} />

      <Button
        variant="outlined"
        onClick={exitEditMode}
        fullWidth
        sx={{
          borderRadius: "999px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.85rem",
          paddingY: "10px",
          borderColor: "grey.300",
          backgroundColor: "background.paper",
          color: "secondary.main",
          "&:hover": {
            backgroundColor: "grey.100",
            borderColor: "secondary.main",
          },
        }}
      >
        Cerrar
      </Button>

      <Button
        variant="contained"
        disabled={selectedSlots.length === 0}
        fullWidth
        onClick={handleApply}
        sx={{
          borderRadius: "999px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.85rem",
          paddingY: "10px",
          backgroundColor:
            selectedSlots.length === 0
              ? (theme) => alpha(theme.palette.primary.main, 0.08)
              : "primary.main",
          color: selectedSlots.length === 0 ? "grey.500" : "white.main",
          boxShadow: "none",
          "&:hover": {
            backgroundColor:
              selectedSlots.length === 0
                ? (theme) => alpha(theme.palette.primary.main, 0.08)
                : "primary.focus",
            boxShadow: "none",
          },
        }}
      >
        Aplicar
      </Button>
    </MDBox>
  );
}
